import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ComponentDefinition, FormDocument} from '@formbird/types';
import {ConfigService} from '@services/config/config.service';
import {FormbirdInjectorService} from '@services/injector/formbird-injector.service';
import {BehaviorSubject} from 'rxjs';
import {SclAceEditorService} from '../../../../formbird-sc-shared/src/lib/scl-ace-editor.service';
import {SclAceTextRulesInterfaceService} from '../../../../formbird-sc-shared/src/lib/scl-ace-text-rules-interface.service';
import {AbstractComponent} from '../../../../formbird-sc-shared/src/lib/components/abstract.component';
import {ChangedDocumentService, NotificationService} from 'formbird-services';
import {MatTooltip} from '@angular/material/tooltip';
import {ComponentSetupService} from '../../../../formbird-sc-shared/src/lib/component-setup.service';
import {AceEditorComponent} from '../../../../ace-edit-wrapper/src/ace-edit-wrapper.component';

// extend the ComponentDefinition type with the additional fields for the component
interface MonacoComponentDefinition extends ComponentDefinition {
  scriptEditMode?: string;
  scriptEditTheme?: string;
  textFormat?: string;
  showGutter?: boolean;
  finaliseValue?: any;
  isWrapMode?: any;
  fontSize?: any;
  liveUpdates?: any;
  viMode?: any;
  aceTextOptions?: any;
}

@Component({
  templateUrl: './monaco-text.component.html',
  styleUrls: ['./monaco-text.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MonacoTextComponent extends AbstractComponent<MonacoComponentDefinition> implements AfterViewInit, OnInit {

  // element
  @ViewChild('elem', {static: false}) aceElement;
  // aceEditor;
  @ViewChild('editor', {static: false}) monacoEditor: any;
  allowTrigger = true;
  isFieldValueChanged = false;
  theme: string;
  mode: string;
  oldValue;
  editorOptions: any;
  aceSession: any;
  tempKeys;
  controlWidth;

  watchDocument: BehaviorSubject<FormDocument>;

  loggedInUserService: any;
  user: any;

  constructor(
    protected formbirdInjectorService: FormbirdInjectorService,
    protected changedDocumentService: ChangedDocumentService,
    private configService: ConfigService,
    private sclAceEditorService: SclAceEditorService,
    private sclAceTextRulesInterfaceService: SclAceTextRulesInterfaceService,
    protected cdr: ChangeDetectorRef,
    protected componentSetupService: ComponentSetupService,
    private notificationService: NotificationService
  ) {
    super(formbirdInjectorService, changedDocumentService, componentSetupService, cdr);
    this.loggedInUserService = this.formbirdInjectorService.get('LoggedInUserService');
  }

  ngOnInit() {
    this.user = this.loggedInUserService.user;

    let language = this.componentDefinition.scriptEditMode === 'nools' ? 'jade' : 
      (this.componentDefinition.scriptEditMode || 'javascript');
    let theme = this.componentDefinition.scriptEditTheme || 'vs-dark';

    this.editorOptions = {
      language: language,
      theme: theme,
      minLines: 10,
      enableSnippets: true,
      wrap: true,
      enableBasicAutocompletion: true,
      enableLiveAutocompletion: true,
      autoScrollEditorIntoView: true,
      showLineNumbers: true,
      fontSize: 18,
      ...this.componentDefinition.aceTextOptions,
      viMode: this.componentDefinition.viMode || this.user.account.viMode,
      viOnWrite: () => {
        const documentSaveService = this.formbirdInjectorService.get('DocumentSaveService');
        documentSaveService.save(this.formParameters.unsavedDocumentListId);
      },
      fieldName: this.componentDefinition.name
    };

    this.editorOptions.fontSize = (this.componentDefinition.fontSize) ? this.componentDefinition.fontSize : 18;

    if (this.componentDefinition.isWrapMode) {
      this.editorOptions.wrap = true;
    }

    this.componentSetupService.setupStandardProperties(this);

    if (typeof this.componentDefinition.scriptEditMode === 'undefined'
      || this.componentDefinition.scriptEditMode === null
      || this.componentDefinition.scriptEditMode.trim() === ''
    ) {
      this.mode = 'javascript';
    } else {
      this.mode = this.componentDefinition.scriptEditMode;
    }

    this.theme = this.componentDefinition.scriptEditTheme?.trim() ?? 'merbivore';

    this.setupChangeDetection();

    const updateSub = this.broadcastService.on('document:updated').subscribe((docs) => {
      let content = docs[0][this.fieldName];
      if (typeof content === 'object') {
        content = JSON.stringify(content, undefined, 2);
      }
      this.updateValueToEditor(content);
    });
    this.subs.add(updateSub);

  }

  onFieldValueChanged(fieldValue) {
    if (this.fieldValue === fieldValue) {
      return;
    }

    setTimeout(() => {
      this.fieldValue = fieldValue;
      console.assert(this.monacoEditor.getEditor() !== undefined, 'monaco editor should not be undefined')
      this.updateValueToEditor(fieldValue);
    });
  }

  setFieldActivation() {
    setTimeout(() => {
      const parentElement = this.monacoEditor.nativeElement.parentElement;
      if (this.disabled) {
        this.aceElement.nativeElement.classList.add('disabled');
        this.aceElement.nativeElement.classList.add('readonly');
        if (parentElement) {
          parentElement.classList.add('disabled');
          parentElement.classList.add('readonly');
        }

      } else {
        this.aceElement.nativeElement.classList.remove('disabled');
        this.aceElement.nativeElement.classList.remove('readonly');
        if (parentElement) {
          parentElement.classList.remove('disabled');
          parentElement.classList.remove('readonly');
        }
      }

      try {
        const div = document.getElementById('gblAceText' + this.fieldName);
        if (div) {
          const lst = div.getElementsByTagName('button');
          // tslint:disable-next-line:prefer-for-of
          for (let i = 0; i < lst.length; i++) {
            const aceEditorElementButton = lst[i] as HTMLButtonElement;
            if (this.disabled) {
              aceEditorElementButton.setAttribute('disabled', this.disabled.toString());
              aceEditorElementButton.style.cursor = 'not-allowed';
            } else {
              aceEditorElementButton.removeAttribute('disabled');
              aceEditorElementButton.style.cursor = 'pointer';
            }
          }
          if (this.monacoEditor) {
            this.monacoEditor.getEditor().setReadOnly(this.disabled);
          }
        }

        this.detectChanges();

      } catch (e) {
        console.error(`****************** error on settingReadOnly  *******************\n${e}`);
      }
    });

  }

  onChange(value) {
    this.valueChanged(value);
  }

  // ACE
  ngAfterViewInit() {
    this.init();

    this.watchDocument = new BehaviorSubject<FormDocument>(this.document);
    this.subs.add(
      this.watchDocument.subscribe(obj => {
        this.watchDocumentFieldName(obj[this.fieldName]);
      })
    );
  }

  init() {

    if (this.document[this.fieldName]) {
      const content = this.document[this.fieldName];
      if (typeof content === 'object') {
        this.monacoEditor.value = JSON.stringify(content, undefined, 2);
      } else {
        this.monacoEditor.value = content;
      }
    }

    // editor.setOptions(editorOptions);
    // editor.setReadOnly(this.disabled);

    // try {
    //   this.sclAceEditorService.enableFullscreen(editor, this.fieldName);
    // } catch (fullscreenerror) {
    //   console.error(`****************** error on setting fullscreen  *******************:\n${fullscreenerror}`);
    // }

    // remove ace editor key binding of 'pageup' and 'pagedown'
    // const keyBinding = editor.keyBinding;
    // const defaultHandler = keyBinding.$defaultHandler;
    // const commandKeyBinding = defaultHandler.commandKeyBinding;
    // for (const key in commandKeyBinding) {
    //   if (key === 'pageup' || key === 'pagedown') {
    //     delete commandKeyBinding[key];
    //   }
    // }

    this.tempKeys = [];

    // const gutter = (this.componentDefinition.showGutter) ? this.componentDefinition.showGutter : true;
    // editor.renderer.setShowGutter(gutter);

    // Process editor contents to document on saving
    const compDef = {...this.componentDefinition};
    compDef.finaliseValue = function () {
      this.allowTrigger = true;
      if (!this.componentDefinition.liveUpdates) {
        this.sclAceTextRulesInterfaceService.extractValuesToDoc(this.document,
          this.fieldName,
          this.componentDefinition,
          this.aceEditor,
          this.tempKeys);
      }
    }.bind(this);
    this.componentDefinition = compDef;

    this.controlWidth = this.getControlWidth(this.componentDefinition.fullWidth, null);
  }

  getControlWidth(fullWidth, type) {
    if (typeof type === 'undefined') {
      if (fullWidth && fullWidth === true) {
        return 'col-md-11';
      } else {
        return 'col-md-9 col-lg-8';
      }
    } else if (type === 'Date') {
      // Special case with single directive date because there is an element that occupies
      // an extra space on the column
      if (fullWidth && fullWidth === true) {
        return 'col-md-10';
      } else {
        return 'col-md-8 col-lg-7';
      }
    }
  }

  configureWorker(e, session) {
    if (session.getMode().$id === 'ace/mode/javascript') {
      if (session.$worker) {
        session.$worker.send('setOptions', [this.configService.clientConfig().jshintConfig]);
      }
    }
  }

  valueChanged(value) {
    if (this.componentDefinition.textFormat === 'raw-json' && value) {
      try {
        value = JSON.parse(value)
        super.valueChanged(value);
      } catch (e) {
        this.notificationService.error(`Invalid JSON in sc-monaco-text (${this.fieldName}): ${e}`)
      }
    } else {
      super.valueChanged(value);
    }
  }

  updateValueToEditor(value) {
    const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    if (this.monacoEditor) {
      this.monacoEditor.value = text;
    }
  }

  watchDocumentFieldName(newValue) {
    if (!this.allowTrigger) {
      return;
    }
    if (typeof newValue === 'string') {
      if (newValue !== this.oldValue) {
        this.updateValueToEditor(newValue);
      }
    } else {
      newValue = JSON.stringify(newValue, undefined, 2);
      if (this.oldValue) {
        this.oldValue = JSON.stringify(this.oldValue, undefined, 2);
      }
      if (newValue !== this.oldValue) {
        this.updateValueToEditor(newValue);
      }
    }
    this.oldValue = newValue;
  }

  showToolTip(tooltip: any, formFieldContainer: HTMLDivElement, labelContainer: any) {
    return (labelContainer.offsetWidth <= formFieldContainer.scrollWidth);
  }

  toggleTooltip(tooltip: MatTooltip) {
    setTimeout(() => {
      tooltip.toggle();
    });
  }
}
